import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  CHILD_TICKET_GROUPS,
  childTicketKindsOf,
  findChildTicketKind,
  type ChildTicketGroupId,
} from '@cortex/domain-product-innovation-lab-basic-design-tree';
import { ChildTicketKindLabel, childTicketToneStyle } from './ChildTicketKindLabel.js';

/**
 * メニュー板の寸法 (px)。**どの群を触っていても変えない**。
 *
 * 群ごとに高さが変わると、下端のクランプ（`menuPlacement`）が板を上へ動かし、
 * 触れている群そのものがポインタの下から逃げて押せなくなる。群は左に固定で積み、
 * 種別は右の決まった枠に出す形にして、板の大きさも位置も動かさない。
 *
 * 高さは群 3 枚（各 36px + 間 8px = 124px）に合わせる。種別は最も多い開発群でも
 * この幅なら 3 行（約 90px）に収まるので、右枠が板を押し広げることはない。
 * @graph-connects none
 */
const GROUPS_WIDTH = 184;
/** @graph-connects none */
const OPTIONS_WIDTH = 372;
/** 群の列と種別の枠の間隔 (px)。 @graph-connects none */
const COLUMN_GAP = 12;
/** 板の内側の余白 (px)。 @graph-connects none */
const PANEL_PADDING = 10;
/** @graph-connects none */
const PANEL_WIDTH = GROUPS_WIDTH + COLUMN_GAP + OPTIONS_WIDTH + PANEL_PADDING * 2;
/** 群 3 枚ぶんの高さ。種別の枠もこれに合わせて最低高を取る。 @graph-connects none */
const COLUMN_HEIGHT = 124;
/** @graph-connects none */
const PANEL_HEIGHT = COLUMN_HEIGHT + PANEL_PADDING * 2;
/** trigger とメニューの間隔 (px)。 @graph-connects none */
const GAP = 8;
/** 画面端との最小余白 (px)。 @graph-connects none */
const VIEWPORT_MARGIN = 8;

/** メニューの置き場所。 */
interface MenuPlacement {
  /** 左端 (px)。 */
  left: number;
  /** 上端 (px)。 */
  top: number;
}

/**
 * trigger の右脇にメニューを置く。右に入らないときは左脇へ返し、
 * 下端からはみ出す分だけ上へ戻す。
 *
 * 板の大きさは定数なので、**開いている間この結果は変わらない**（群を触り替えても
 * 板が動かない）。テストから直接検証できるよう named export にしている。
 * @graph-connects none
 */
export function menuPlacement(
  trigger: { left: number; right: number; top: number },
  viewport: { width: number; height: number },
): MenuPlacement {
  const right = trigger.right + GAP;
  const fitsRight = right + PANEL_WIDTH + VIEWPORT_MARGIN <= viewport.width;
  const left = fitsRight ? right : Math.max(trigger.left - GAP - PANEL_WIDTH, VIEWPORT_MARGIN);
  const maxTop = viewport.height - PANEL_HEIGHT - VIEWPORT_MARGIN;
  return {
    left,
    top: Math.min(Math.max(trigger.top, VIEWPORT_MARGIN), Math.max(maxTop, VIEWPORT_MARGIN)),
  };
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business 子票種別を選ぶメニュー。ラベルを押すか表のセルで Enter を押すと右脇に 3 つの群が上から順に軽く弾んで出て、上下キーで群・左右キーで種別を渡り歩き Enter で決まる。下地は敷かず、背景は透けたままにする
 * @graph-connects menuPlacement [calls] trigger 脇へのメニュー配置
 * @graph-connects childTicketKindsOf [calls] 触れている群の種別一覧
 * @graph-connects findChildTicketKind [calls] 選択中の種別の解決
 */
export function ChildTicketKindPicker({
  value,
  onChange,
  rowLabel,
  open,
  onOpenChange,
}: {
  /** 選択中の種別 id。 */
  value: string;
  /** 選択ハンドラ。 */
  onChange: (kindId: string) => void;
  /** aria-label に混ぜる行の呼び名（「3 行目の種別」のように読ませる）。 */
  rowLabel: string;
  /** メニューを開いているか。表のセルから Enter で開けるよう、開閉は外が持つ。 */
  open: boolean;
  /** 開閉ハンドラ。 */
  onOpenChange: (open: boolean) => void;
}): ReactNode {
  const { t } = useTranslation();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const groupRefs = useRef<(HTMLButtonElement | null)[]>([]);
  // 群を渡った直後は、新しい群の札が描かれてから焦点を当てる必要がある
  const pendingFocus = useRef<'first' | 'last' | null>(null);
  // 矢印キーで動かしている最中かどうか（下の onMouseEnter を黙らせる印）
  const keyboardNav = useRef(false);
  const focusedOnOpen = useRef(false);
  const [place, setPlace] = useState<MenuPlacement | null>(null);
  const selected = findChildTicketKind(value);
  // 種別が差し替わったことを弾む動きで見せるための札。描いた回数ではなく
  // **値が変わった回数**を数える（最初の描画では弾ませない。13 行が一斉に
  // 跳ねると、何かが変わったようにしか見えない）。
  const previousValue = useRef(value);
  const [bump, setBump] = useState(0);
  useEffect(() => {
    if (previousValue.current === value) return;
    previousValue.current = value;
    setBump((count) => count + 1);
  }, [value]);
  const [activeGroup, setActiveGroup] = useState<ChildTicketGroupId>('dev');

  // 開いたときは選択中の種別が属する群を開いて見せる（今どこに居るかが分かる）。
  useEffect(() => {
    if (open) setActiveGroup(selected?.group ?? 'dev');
  }, [open, selected?.group]);

  // 表はスクロールするので scroll / resize で位置を取り直す。
  useLayoutEffect(() => {
    if (!open) {
      setPlace(null);
      return;
    }
    const update = (): void => {
      const trigger = triggerRef.current;
      if (trigger === null) return;
      const rect = trigger.getBoundingClientRect();
      setPlace(menuPlacement(rect, { width: window.innerWidth, height: window.innerHeight }));
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open]);

  // 外側クリックと Escape で閉じる。表のセル内に居るので、閉じ忘れると下の行が押せない。
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent): void => {
      const target = e.target as Node | null;
      if (target === null) return;
      if (triggerRef.current?.contains(target) === true) return;
      if (menuRef.current?.contains(target) === true) return;
      onOpenChange(false);
    };
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onOpenChange(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onOpenChange]);

  // 板が出きってから、今選ばれている種別の札へ焦点を置く。ここに居れば左右キーで
  // すぐ隣の種別へ、上下キーで別の群へ渡れる（開いた直後からキーボードだけで決まる）。
  const placed = place !== null;
  useEffect(() => {
    if (!open || !placed) {
      focusedOnOpen.current = false;
      return;
    }
    if (focusedOnOpen.current) return;
    focusedOnOpen.current = true;
    const kinds = childTicketKindsOf(activeGroup);
    const index = kinds.findIndex((kind) => kind.id === value);
    optionRefs.current[index >= 0 ? index : 0]?.focus();
  }, [open, placed, activeGroup, value]);

  // 群を渡ると札が描き替わるので、描かれてから焦点を移す（描画前に focus しても効かない）。
  useLayoutEffect(() => {
    const target = pendingFocus.current;
    if (target === null) return;
    pendingFocus.current = null;
    const length = childTicketKindsOf(activeGroup).length;
    optionRefs.current[target === 'last' ? length - 1 : 0]?.focus();
  });

  /** 今いる札の番号（どこにも居なければ 0 扱い）。 @graph-connects none */
  const currentOptionIndex = (): number => {
    const index = optionRefs.current.findIndex((el) => el === document.activeElement);
    return index >= 0 ? index : 0;
  };

  /** 今いる群の番号。 @graph-connects none */
  const activeGroupIndex = (): number =>
    CHILD_TICKET_GROUPS.findIndex((group) => group.id === activeGroup);

  /** 左の群の列に居るか（種別の札ではなく群のボタンに焦点がある）。 @graph-connects none */
  const isOnGroupColumn = (): boolean =>
    groupRefs.current.some((el) => el !== null && el === document.activeElement);

  /**
   * 群の列の中の行き来。上下で群を移り、右で種別の札へ入る。
   *
   * 群のボタンは onFocus で `activeGroup` を立てるので、ここは焦点を移すだけでよい
   * （状態と見えているものが 1 本の経路で揃う）。
   * @graph-connects none
   */
  const onGroupColumnKey = (key: string): void => {
    if (key === 'ArrowUp' || key === 'ArrowDown') {
      const next = Math.min(
        Math.max(activeGroupIndex() + (key === 'ArrowDown' ? 1 : -1), 0),
        CHILD_TICKET_GROUPS.length - 1,
      );
      groupRefs.current[next]?.focus();
      return;
    }
    if (key === 'ArrowRight') optionRefs.current[0]?.focus();
  };

  /**
   * 上下キーの行き先。札は折り返して並ぶので、**まず同じ群の次の段**へ送り、
   * 段が尽きたときだけ隣の群へ渡す。上下を押すたびに群が飛ぶと、2 段目以降の
   * 種別にキーボードだけでは辿り着けない。
   * @graph-connects none
   */
  const moveByRow = (step: 1 | -1): void => {
    const buttons = optionRefs.current
      .slice(0, childTicketKindsOf(activeGroup).length)
      .filter((el): el is HTMLButtonElement => el !== null);
    const index = currentOptionIndex();
    const from = buttons[index];
    if (from !== undefined) {
      // 折り返しの段は offsetTop で分かる（同じ段の札は同じ offsetTop になる）
      const rows = [...new Set(buttons.map((el) => el.offsetTop))].sort((a, b) => a - b);
      const rowIndex = rows.indexOf(from.offsetTop);
      const nextRow = rows[rowIndex + step];
      if (nextRow !== undefined) {
        // 同じ段の中では、横の位置がいちばん近い札へ送る（見た目どおりに動く）
        const center = from.offsetLeft + from.offsetWidth / 2;
        const target = buttons
          .filter((el) => el.offsetTop === nextRow)
          .reduce((best, el) =>
            Math.abs(el.offsetLeft + el.offsetWidth / 2 - center) <
            Math.abs(best.offsetLeft + best.offsetWidth / 2 - center)
              ? el
              : best,
          );
        target.focus();
        return;
      }
    }
    const groups = CHILD_TICKET_GROUPS;
    const current = groups.findIndex((group) => group.id === activeGroup);
    const group = groups[current + step];
    if (group === undefined) return;
    setActiveGroup(group.id);
    // 下へ出たら次の群の先頭、上へ出たら前の群の末尾（行き来が繋がって見える）
    pendingFocus.current = step === 1 ? 'first' : 'last';
  };

  // 上下は段送り（尽きたら群を渡る）、左右は並び順の隣。Enter は札そのものが受ける。
  const onMenuKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    // 下の表のセル移動へは渡さない（メニューの中の操作）
    e.stopPropagation();
    // 群のボタンに居るときの Enter は「その群の札へ入る」。ボタンの既定の活性化
    // （= activeGroup を立て直すだけ）に任せると、何も起きていないように見える
    if (e.key === 'Enter' && isOnGroupColumn()) {
      e.preventDefault();
      optionRefs.current[0]?.focus();
      return;
    }
    if (!e.key.startsWith('Arrow')) return;
    e.preventDefault();
    keyboardNav.current = true;
    if (isOnGroupColumn()) {
      onGroupColumnKey(e.key);
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      moveByRow(e.key === 'ArrowDown' ? 1 : -1);
      return;
    }
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      const index = currentOptionIndex();
      // 左端からさらに左は、群の列へ戻る（そこから上下で群を選び、右でまた札へ入る）
      if (e.key === 'ArrowLeft' && index === 0) {
        groupRefs.current[activeGroupIndex()]?.focus();
        return;
      }
      const kinds = childTicketKindsOf(activeGroup);
      const next = Math.min(
        Math.max(index + (e.key === 'ArrowRight' ? 1 : -1), 0),
        kinds.length - 1,
      );
      optionRefs.current[next]?.focus();
    }
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={t('childTicketPlan.kindPicker.trigger', { row: rowLabel })}
        // 表のセル側がキーボードの行き先を持つので、trigger は Tab の順路に入れない
        tabIndex={-1}
        onClick={() => {
          onOpenChange(!open);
        }}
        className="child-kind-trigger max-w-full cursor-pointer rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-border-focus)]"
      >
        {selected === undefined ? (
          <span className="rounded-full border border-dashed border-[var(--color-border)] px-3 py-1.5 text-xs text-[var(--color-text-muted)]">
            {t('childTicketPlan.kindPicker.empty')}
          </span>
        ) : (
          // key を差し替えて札を描き直し、動きを頭から流し直す（同じ要素のままだと
          // 2 回目以降のアニメーションが走らない）
          <span
            key={bump}
            className={`inline-flex max-w-full ${bump > 0 ? 'child-kind-bump' : ''}`}
          >
            <ChildTicketKindLabel label={selected.label} tone={selected.tone} />
          </span>
        )}
      </button>

      {open && place !== null
        ? createPortal(
            <div
              ref={menuRef}
              role="dialog"
              aria-label={t('childTicketPlan.kindPicker.menu')}
              // 下地はすりガラス。完全に透かすと下の表の罫線と入力欄に札が重なって
              // 読めなくなるので、背後をぼかしつつ透かす（.child-ticket-glass）。
              onKeyDown={onMenuKeyDown}
              onPointerMove={() => {
                keyboardNav.current = false;
              }}
              className="child-ticket-glass fixed flex rounded-2xl"
              // 表のセル選択を解く判定から外すための目印（種別を選んでいる間に
              // セル選択が消えると、閉じた直後に方向キーが効かなくなる）。
              data-child-ticket-overlay="true"
              style={{
                left: place.left,
                top: place.top,
                width: PANEL_WIDTH,
                height: PANEL_HEIGHT,
                padding: PANEL_PADDING,
                gap: COLUMN_GAP,
                // 重なり順は共有デザインシステムの popover と同じ段に置く。Tailwind の
                // 任意値クラスにすると、変数が出力から落ちたとき z-index: auto に化けて
                // サイドバー（--z-sticky）の下に潜るので、fallback 付きで直に指定する。
                zIndex: 'var(--z-toast, 300)',
              }}
            >
              {/* 群は左に固定で積む。触り替えても 1px も動かないので、狙った群を押し損ねない */}
              <div
                className="flex shrink-0 flex-col justify-between"
                style={{ width: GROUPS_WIDTH }}
              >
                {CHILD_TICKET_GROUPS.map((group, groupIndex) => {
                  const isActive = group.id === activeGroup;
                  return (
                    <button
                      key={group.id}
                      ref={(el) => {
                        groupRefs.current[groupIndex] = el;
                      }}
                      type="button"
                      data-active={isActive}
                      onMouseEnter={() => {
                        // メニューはセルの右脇に出るので、開いた時点でポインタが板の上に
                        // 乗っていることがある。そのまま hover を受けると、キーで群を移した
                        // 直後に札が入れ替わって mouseover が走り、ポインタの下の群へ
                        // 引き戻される（キーボードだけでは群を移れなくなる）。実際に
                        // マウスが動くまでは手を出さない。
                        if (keyboardNav.current) return;
                        setActiveGroup(group.id);
                      }}
                      onFocus={() => {
                        setActiveGroup(group.id);
                      }}
                      onClick={() => {
                        setActiveGroup(group.id);
                      }}
                      className="child-kind-group w-full cursor-pointer rounded-full px-4 py-2 text-sm font-bold focus:outline-none"
                      style={{
                        ...childTicketToneStyle(group.tone),
                        animationDelay: `${String(groupIndex * 55)}ms`,
                      }}
                    >
                      {t(group.labelKey)}
                    </button>
                  );
                })}
              </div>

              {/* 触れている群の種別。枠は固定なので、中身が変わっても板は伸び縮みしない */}
              <div
                className="flex flex-wrap content-start gap-1.5 overflow-y-auto"
                style={{ width: OPTIONS_WIDTH }}
              >
                {childTicketKindsOf(activeGroup).map((kind, i) => (
                  <button
                    key={kind.id}
                    type="button"
                    ref={(el) => {
                      optionRefs.current[i] = el;
                    }}
                    onClick={() => {
                      onChange(kind.id);
                      onOpenChange(false);
                    }}
                    aria-pressed={kind.id === value}
                    className="child-kind-option h-fit cursor-pointer rounded-full px-3 py-1 text-[11px] font-semibold focus:outline-none"
                    style={{
                      ...childTicketToneStyle(kind.tone),
                      animationDelay: `${String(i * 22)}ms`,
                    }}
                  >
                    {kind.label}
                  </button>
                ))}
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
